import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { StatCard } from '../components/StatCard';
import { AssetCard } from '../components/AssetCard';

import { PortfolioAnalytics } from '../components/PortfolioAnalytics';
import { api } from '../services/api';
import { useCurrency } from '../context/CurrencyContext';
import { ASSET_CATEGORIES } from '../utils/helpers';
import { 
  Wallet, 
  TrendingUp, 
  Briefcase, 
  Calendar,
  Plus,
  ArrowRight,
  Car,
  Palette,
  Wine,
  Gem,
  ShoppingBag,
  Watch,
  BarChart3,
  FileText,
  Download,
  FolderLock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ProfileCompletionBanner } from '../components/ProfileCompletionBanner';
import { toast } from 'sonner';

const iconMap = {
  car: Car,
  art: Palette,
  wine: Wine,
  jewelry: Gem,
  handbag: ShoppingBag,
  watch: Watch,
};

export default function Dashboard() {
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [vault, setVault] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, assetsRes, maintenanceRes, performanceRes, vaultRes] = await Promise.all([
        api.getPortfolioSummary(),
        api.getAssets(),
        api.getMaintenance(),
        api.getPortfolioPerformance(),
        api.getVault().catch(() => ({ data: null })),
      ]);
      setSummary(summaryRes.data);
      setPerformance(performanceRes.data);
      setAssets(assetsRes.data);
      setMaintenance(maintenanceRes.data.filter(m => m.status === 'scheduled').slice(0, 3));
      setVault(vaultRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await api.downloadPortfolioReport();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assagart_portfolio_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Portfolio report downloaded!');
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloadingReport(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="dashboard-loading">
          <div className="mb-8">
            <Skeleton className="h-3 w-32 mb-3 bg-gold/10" />
            <Skeleton className="h-9 w-48 bg-white/5" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 bg-white/5" />
            ))}
          </div>
          <Skeleton className="h-64 mb-8 bg-white/5" />
          <Skeleton className="h-3 w-40 mb-4 bg-gold/10" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 bg-white/5" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const recentAssets = assets.slice(0, 4);
  const gainTrend = summary?.gain_percentage >= 0 ? 'up' : 'down';

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Portfolio Overview</p>
            <h1 className="font-serif text-3xl text-white">Your Vault</h1>
          </div>
          <div className="flex gap-2">
            {assets.length > 0 && (
              <Button 
                onClick={handleDownloadReport}
                className="btn-secondary flex items-center gap-2"
                disabled={downloadingReport}
                data-testid="download-report-btn"
              >
                {downloadingReport ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <FileText size={16} />
                )}
                {downloadingReport ? 'Generating...' : 'PDF Report'}
              </Button>
            )}
            <Link to="/assets/new">
              <Button className="btn-primary flex items-center gap-2" data-testid="add-asset-btn">
                <Plus size={16} />
                Add Asset
              </Button>
            </Link>
          </div>
        </div>

        {/* Profile completion nudge */}
        <ProfileCompletionBanner />

        {/* Stats Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            title="Total Value" 
            value={formatCurrency(summary?.total_value || 0)}
            icon={Wallet}
          />
          <StatCard 
            title="Total Gain/Loss" 
            value={formatCurrency(summary?.total_gain || 0)}
            subtitle={`${summary?.gain_percentage >= 0 ? '+' : ''}${summary?.gain_percentage || 0}%`}
            icon={TrendingUp}
            trend={gainTrend}
          />
          <StatCard 
            title="Total Assets" 
            value={summary?.total_assets || 0}
            icon={Briefcase}
          />
          <StatCard 
            title="Upcoming Maintenance" 
            value={maintenance.length}
            subtitle="Scheduled"
            icon={Calendar}
          />
        </div>

        {/* Portfolio Analytics - Moved up for visibility */}
        {assets.length > 0 && (
          <div className="mb-8">
            <PortfolioAnalytics 
              summary={summary} 
              assets={assets} 
              performance={performance} 
            />
          </div>
        )}

        {/* Vault completeness callout */}
        {assets.length > 0 && vault && (() => {
          const totalAssets = assets.length;
          const coveredAssets = Object.keys(vault.summary?.counts_by_asset || {}).length;
          const pct = totalAssets > 0 ? Math.round((coveredAssets / totalAssets) * 100) : 0;
          const tone = pct >= 80 ? 'emerald' : pct >= 50 ? 'gold' : pct >= 25 ? 'amber' : 'red';
          const barClass = tone === 'emerald' ? 'bg-emerald-400' : tone === 'gold' ? 'bg-gold' : tone === 'amber' ? 'bg-amber-400' : 'bg-red-400';
          const ringClass = tone === 'emerald' ? 'border-emerald-500/30' : tone === 'gold' ? 'border-gold/30' : tone === 'amber' ? 'border-amber-500/30' : 'border-red-500/30';
          const textClass = tone === 'emerald' ? 'text-emerald-400' : tone === 'gold' ? 'text-gold' : tone === 'amber' ? 'text-amber-400' : 'text-red-400';
          return (
            <Link
              to="/vault"
              data-testid="vault-completeness-callout"
              className={`block luxury-card p-5 mb-8 border ${ringClass} hover:border-gold/40 transition-colors group`}
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className={`w-12 h-12 rounded flex items-center justify-center bg-gradient-to-br from-white/5 to-transparent ${ringClass} border`}>
                    <FolderLock size={20} className={textClass} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Vault completeness</p>
                    <p className="text-white text-sm">
                      <span className="font-serif text-2xl mr-2">{coveredAssets}<span className="text-muted-foreground">/{totalAssets}</span></span>
                      <span className="text-xs text-muted-foreground">assets have documents on file</span>
                    </p>
                    <div className="h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden mt-2 max-w-md">
                      <div className={`h-full ${barClass} transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-serif text-3xl ${textClass}`} data-testid="vault-completeness-pct">{pct}%</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1 group-hover:text-gold transition-colors">
                    Open Vault →
                  </p>
                </div>
              </div>
              {pct < 100 && (
                <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                  Upload at least an insurance schedule and a valuation for every asset.
                  Well-documented vaults make claims faster, sales easier, and inheritance cleaner.
                </p>
              )}
            </Link>
          );
        })()}

        {/* Performance vs Market */}
        {performance && performance.top_performers && performance.top_performers.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Top Performers */}
            <div className="luxury-card p-6" data-testid="top-performers">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={18} className="text-emerald-400" />
                <h2 className="font-serif text-lg text-white">Top Performers</h2>
              </div>
              <div className="space-y-3">
                {performance.top_performers.map((asset, idx) => {
                  const Icon = iconMap[asset.category];
                  return (
                    <Link 
                      key={asset.id}
                      to={`/assets/${asset.id}`}
                      className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-emerald-500/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg text-muted-foreground font-mono">#{idx + 1}</span>
                        {Icon && <Icon size={16} className="text-gold" />}
                        <div>
                          <p className="text-sm text-white">{asset.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{asset.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-emerald-400 font-mono">+{asset.performance}%</p>
                        <p className="text-xs text-muted-foreground">{formatCurrency(asset.gain)}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Market Comparison */}
            {performance.performance_vs_market && Object.keys(performance.performance_vs_market).length > 0 && (
              <div className="luxury-card p-6" data-testid="market-comparison">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 size={18} className="text-gold" />
                  <h2 className="font-serif text-lg text-white">Portfolio vs Market</h2>
                </div>
                <div className="space-y-3">
                  {Object.entries(performance.performance_vs_market).map(([category, data]) => {
                    const Icon = iconMap[category];
                    const isOutperforming = data.outperforming;
                    return (
                      <div 
                        key={category}
                        className="p-3 bg-[#0A0A0A] border border-[#1A1A1A]"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {Icon && <Icon size={14} className="text-gold" />}
                            <span className="text-sm text-white capitalize">{category}</span>
                          </div>
                          <span className={`text-xs px-2 py-0.5 ${isOutperforming ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {isOutperforming ? 'Outperforming' : 'Underperforming'}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground">Your portfolio: <span className={`${data.portfolio_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{data.portfolio_change >= 0 ? '+' : ''}{data.portfolio_change.toFixed(1)}%</span></span>
                          <span className="text-muted-foreground">Market: <span className={`${data.index_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{data.index_change >= 0 ? '+' : ''}{data.index_change.toFixed(1)}%</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category Breakdown */}
        {summary?.category_breakdown && Object.keys(summary.category_breakdown).length > 0 && (
          <div className="luxury-card p-6 mb-8">
            <h2 className="font-serif text-xl text-white mb-6">Asset Allocation</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {ASSET_CATEGORIES.map((cat) => {
                const data = summary.category_breakdown[cat.value];
                const Icon = iconMap[cat.value];
                if (!data) return null;
                return (
                  <div 
                    key={cat.value} 
                    className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors"
                    data-testid={`allocation-${cat.value}`}
                  >
                    <Icon size={20} className="text-gold mb-3" />
                    <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{cat.label}</p>
                    <p className="text-lg text-white font-semibold">{formatCurrency(data.value)}</p>
                    <p className="text-xs text-muted-foreground">{data.count} asset{data.count !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Assets */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-xl text-white">Recent Assets</h2>
            <Link to="/assets" className="text-gold text-sm hover:underline flex items-center gap-1" data-testid="view-all-assets">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          
          {recentAssets.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentAssets.map((asset) => (
                <AssetCard key={asset.id} asset={asset} />
              ))}
            </div>
          ) : (
            <div className="luxury-card p-12 text-center">
              <Briefcase size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="font-serif text-xl text-white mb-2">Your vault is empty</h3>
              <p className="text-muted-foreground mb-6">Add your first luxury asset to get started</p>
              <Link to="/assets/new">
                <Button className="btn-secondary" data-testid="empty-add-asset-btn">
                  Add Your First Asset
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Upcoming Maintenance */}
        {maintenance.length > 0 && (
          <div className="luxury-card p-6">
            <h2 className="font-serif text-xl text-white mb-6">Upcoming Maintenance</h2>
            <div className="space-y-3">
              {maintenance.map((item) => {
                const asset = assets.find(a => a.id === item.asset_id);
                return (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#1A1A1A]"
                    data-testid={`maintenance-${item.id}`}
                  >
                    <div>
                      <p className="text-white font-medium">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{asset?.name || 'Unknown Asset'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gold">{new Date(item.scheduled_date).toLocaleDateString()}</p>
                      <p className="text-xs text-muted-foreground capitalize">{item.status}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
